import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import '../styles/Auth.css';
import signinGraphic from '../icons/signin.png';

// Google's official mark. Rendered inline (not via an icon set) because
// Google's brand guidelines require the exact four-colour logo, and the
// design system's Lucide-only rule covers UI icons, not third-party marks.
const GoogleMark: React.FC = () => (
  <svg viewBox="0 0 18 18" aria-hidden="true" focusable="false">
    <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.91c1.7-1.57 2.69-3.88 2.69-6.62z" />
    <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.91-2.26c-.81.54-1.84.86-3.05.86-2.34 0-4.33-1.58-5.04-3.71H.96v2.33A9 9 0 0 0 9 18z" />
    <path fill="#FBBC05" d="M3.96 10.71a5.41 5.41 0 0 1 0-3.42V4.96H.96a9 9 0 0 0 0 8.08l3-2.33z" />
    <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.96l3 2.33C4.67 5.16 6.66 3.58 9 3.58z" />
  </svg>
);

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn, signInWithGoogle, authError, clearAuthError } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    clearAuthError();
    setLoading(true);

    const { error } = await signIn(email, password);
    // Clear password from state immediately — do not hold credentials longer than needed
    setPassword('');

    if (error) {
      const msg = error.message.toLowerCase();
      if (msg.includes('email not confirmed') || msg.includes('not confirmed')) {
        setError('Please confirm your email first — check your inbox for the confirmation link.');
      } else if (msg.includes('invalid login') || msg.includes('invalid credentials')) {
        setError('Incorrect email or password.');
      } else {
        setError(error.message);
      }
      setLoading(false);
    } else {
      navigate('/dashboard');
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);

    const { error } = await signInWithGoogle();

    if (error) {
      setError(error.message);
      setLoading(false);
    }
    // Note: On success, Supabase will redirect to the OAuth provider
    // and then back to our redirectTo URL
  };

  return (
    <div className="auth-container-new">
      <div className="auth-left">
        <div className="auth-left-content">
          <h1>The workspace for product teams</h1>
          <p>Plan, write and decide in one place — from first insight to shipped release.</p>
          <div className="auth-graphic">
            <img src={signinGraphic} alt="Workspace illustration" />
          </div>
        </div>
      </div>

      <div className="auth-right">
        <div className="auth-right-content">
          <div className="auth-greeting">
            <h2>Hello!</h2>
            <p>We are glad to see you :)</p>
          </div>

          <div className="auth-buttons-row">
            <button className="google-btn" onClick={handleGoogleSignIn} type="button" disabled={loading}>
              <GoogleMark />
              Sign in with Google
            </button>
            <Link to="/signup" className="switch-btn">
              Sign Up
            </Link>
          </div>

          <div className="auth-divider">Or</div>

          <form onSubmit={handleSubmit} className="auth-form-new">
            {authError && <div className="error-message">{authError}</div>}
            {error && <div className="error-message">{error}</div>}

            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              autoComplete="email"
              required
              disabled={loading}
              className="auth-input"
            />

            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              autoComplete="current-password"
              required
              disabled={loading}
              className="auth-input"
            />

            <div className="forgot-password">
              <Link to="/forgot-password">Forgot password?</Link>
            </div>

            <button type="submit" className="auth-submit-btn" disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
